import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, InputGroup, Spinner } from 'react-bootstrap';
import { ArrowLeft, GeoAlt, PersonFill, Robot, Search, Tools } from 'react-bootstrap-icons';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { clientService } from '@/services/clientService';
import {
  attachIpsToConversations,
  fetchConversationTrace,
  fetchProxyRequestEvents,
  listPublicDemoConversations,
} from '@/services/publicDemoConversationsService';
import { CopyButton } from '@/components/ui/CopyButton';
import { PageHeader } from '@/components/ui/PageHeader';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import type { Client } from '@/types';
import type { ConversationDetail, ConversationSummary, ParsedTraceEvent } from '@/types/publicDemoConversation';
import './PublicDemoConversations.css';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A message as the visitor or Numa wrote it. Links open in a new tab. */
function MessageMarkdown({ text }: { text: string }) {
  return (
    <div className="demo-conv-md">
      <ReactMarkdown
        components={{
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

function EventRow({ event }: { event: ParsedTraceEvent }) {
  if (event.kind === 'user') {
    return (
      <div className="demo-conv-turn demo-conv-turn--visitor">
        <div className="demo-conv-bubble demo-conv-bubble--visitor">
          <div className="demo-conv-bubble__who">
            <PersonFill size={12} aria-hidden="true" />
            Visitor
            <time dateTime={event.timestamp} title={formatNzDateTime(event.timestamp)}>
              {timeAgo(event.timestamp)}
            </time>
          </div>
          <MessageMarkdown text={event.text} />
        </div>
      </div>
    );
  }
  if (event.kind === 'assistant') {
    return (
      <div className="demo-conv-turn">
        <div className="demo-conv-bubble demo-conv-bubble--numa">
          <div className="demo-conv-bubble__who">
            <Robot size={12} aria-hidden="true" />
            Numa
            <time dateTime={event.timestamp} title={formatNzDateTime(event.timestamp)}>
              {timeAgo(event.timestamp)}
            </time>
          </div>
          <MessageMarkdown text={event.text} />
        </div>
      </div>
    );
  }
  // tool_call
  return (
    <div className="demo-conv-turn">
      <div className="demo-conv-tool" title={event.paramsSummary}>
        <Tools size={12} aria-hidden="true" />
        <span className="demo-conv-tool__name">{event.toolName}</span>
        <span className="demo-conv-tool__params">{event.paramsSummary}</span>
      </div>
    </div>
  );
}

export default function PublicDemoConversations() {
  const { clientName } = useParams<{ clientName: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Load the client record + its conversations.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientName) return;
      setLoadingList(true);
      setListError(null);
      try {
        const allClients = await clientService.getAllClients();
        const match = allClients.find((c) => c.name === clientName);
        if (!match) {
          throw new Error(`There is no client named "${clientName}".`);
        }
        if (!match.config.publicDemo) {
          throw new Error(`"${clientName}" is not set up as a public demo, so it has no visitor conversations.`);
        }
        if (cancelled) return;
        setClient(match);
        const [list, proxyEvents] = await Promise.all([
          listPublicDemoConversations(match),
          // Best-effort: if the Insights query fails, carry on without IPs.
          fetchProxyRequestEvents(match, 30).catch((err) => {
            console.warn('[PublicDemoConversations] IP enrichment failed:', err);
            return [];
          }),
        ]);
        if (cancelled) return;
        const enriched = attachIpsToConversations(list, proxyEvents);
        setConversations(enriched);
        if (enriched.length > 0) setSelectedId(enriched[0].conversationId);
      } catch (e) {
        if (cancelled) return;
        setListError(e instanceof Error ? e.message : 'The conversations did not load.');
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clientName]);

  // Load the selected conversation's trace.
  const loadDetail = useCallback(
    async (id: string) => {
      if (!client) return;
      setLoadingDetail(true);
      setDetailError(null);
      setDetail(null);
      try {
        const d = await fetchConversationTrace(client, id);
        setDetail(d);
      } catch (e) {
        setDetailError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        setLoadingDetail(false);
      }
    },
    [client]
  );

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter(
      (c) => c.conversationId.toLowerCase().includes(q) || (c.sourceIp ?? '').toLowerCase().includes(q)
    );
  }, [conversations, search]);

  const selectedSummary = useMemo(
    () => conversations.find((c) => c.conversationId === selectedId),
    [conversations, selectedId]
  );

  return (
    <div className="demo-conv-page">
      <PageHeader
        title="Public demo conversations"
        actions={
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            <ArrowLeft className="me-1" />
            Back
          </Button>
        }
      />

      {listError && <Alert variant="danger">{listError}</Alert>}

      <div className="demo-conv-layout">
        {/* ── The conversations ── */}
        <div className="demo-conv-side">
          <InputGroup className="demo-conv-search">
            <InputGroup.Text>
              <Search />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search by id or IP address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search conversations"
            />
          </InputGroup>

          <Card className="demo-conv-list">
            {loadingList ? (
              <div className="demo-conv-empty">
                <Spinner animation="border" size="sm" className="me-2" />
                Loading conversations
              </div>
            ) : filtered.length === 0 ? (
              <div className="demo-conv-empty">
                {listError
                  ? 'No conversations to show.'
                  : conversations.length === 0
                    ? 'No visitor has used this demo yet.'
                    : 'No conversation matches that search.'}
              </div>
            ) : (
              <ul className="demo-conv-items">
                {filtered.map((c) => {
                  const active = c.conversationId === selectedId;
                  return (
                    <li key={c.conversationId}>
                      <button
                        type="button"
                        className={`demo-conv-item${active ? ' is-active' : ''}`}
                        aria-current={active ? 'true' : undefined}
                        onClick={() => setSelectedId(c.conversationId)}
                      >
                        <span className="demo-conv-item__top">
                          <span className="mono demo-conv-item__id" title={c.conversationId}>
                            {c.conversationId.slice(0, 8)}…
                          </span>
                          <span className="demo-conv-item__when" title={formatNzDateTime(c.lastModified)}>
                            {timeAgo(c.lastModified)}
                          </span>
                        </span>
                        <span className="demo-conv-item__meta">
                          <span>{formatNzDateTime(c.lastModified)}</span>
                          <span className="numeric" title="The size of the saved conversation">
                            {(c.traceBytes / 1024).toFixed(1)} KB
                          </span>
                        </span>
                        {c.sourceIp && (
                          <span className="demo-conv-item__ip mono">
                            <GeoAlt size={11} aria-hidden="true" />
                            {c.sourceIp}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          {!loadingList && conversations.length > 0 && (
            <p className="demo-conv-count">
              {filtered.length === conversations.length
                ? count(conversations.length, 'conversation', 'conversations')
                : `${filtered.length} of ${conversations.length} match`}
            </p>
          )}
        </div>

        {/* ── The open conversation ── */}
        <Card className="demo-conv-detail">
          {!selectedId ? (
            <div className="demo-conv-empty">{loadingList ? '' : 'Pick a conversation on the left to read it.'}</div>
          ) : loadingDetail ? (
            <div className="demo-conv-empty">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading the conversation
            </div>
          ) : detailError ? (
            <Alert variant="danger" className="demo-conv-detail__error">
              <span>This conversation did not load: {detailError}</span>
              <Button variant="outline-danger" size="sm" onClick={() => loadDetail(selectedId)}>
                Try again
              </Button>
            </Alert>
          ) : detail ? (
            <>
              <div className="demo-conv-detail__head">
                <div>
                  <div className="section-label demo-conv-detail__label">Conversation</div>
                  <div className="demo-conv-detail__id">
                    <span className="mono">{detail.conversationId}</span>
                    <CopyButton value={detail.conversationId} label="Copy conversation id" />
                  </div>
                  {selectedSummary?.sourceIp && (
                    <div className="demo-conv-detail__ip mono">
                      <GeoAlt size={12} aria-hidden="true" />
                      {selectedSummary.sourceIp}
                    </div>
                  )}
                </div>
                <div className="demo-conv-detail__counts">
                  {count(detail.events.filter((e) => e.kind === 'user').length, 'visitor message', 'visitor messages')}
                  {' · '}
                  {count(detail.events.filter((e) => e.kind === 'assistant').length, 'reply', 'replies')}
                  {' · '}
                  {count(detail.events.filter((e) => e.kind === 'tool_call').length, 'tool call', 'tool calls')}
                </div>
              </div>
              <div className="demo-conv-detail__body">
                {detail.events.length === 0 ? (
                  <div className="demo-conv-empty">This conversation holds no messages or tool calls to show.</div>
                ) : (
                  detail.events.map((ev, i) => <EventRow key={i} event={ev} />)
                )}
              </div>
            </>
          ) : null}
        </Card>
      </div>
    </div>
  );
}
